import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import { useSettings } from "../context/SettingsContext";
import DashboardService from "../services/DashboardService";
import FeedBackService from "../services/FeedbackService";

const feedbackTypes = [
  { label: "Öneri", value: "suggestion", icon: "pi pi-lightbulb", color: "text-amber-400" },
  { label: "Hata", value: "bug", icon: "pi pi-exclamation-triangle", color: "text-rose-400" },
  { label: "İstek", value: "request", icon: "pi pi-star", color: "text-indigo-400" }
];

export default function Dashboard() {
  const { settings } = useSettings();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const navigate = useNavigate();
  const toast = useRef(null);

  const [stats, setStats] = useState({
    todaySales: 0,
    monthSales: 0,
    todayCollections: 0,
    monthCollections: 0,
    todayProfit: 0,
    monthProfit: 0,
    totalCustomers: 0,
    totalProducts: 0,
    inventoryCostValue: 0,
    inventorySaleValue: 0,
    monthlySalesTarget: 0,
    newCustomersCount: 0,
    averageOrderValue: 0,
    recentSales: [],
    criticalStockProducts: [],
    topSellingProducts: [],
    highImpactCustomers: [],
    salesChartData: [],
    upcomingExpenses: []
  });

  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [category, setCategory] = useState(feedbackTypes[0]);
  const [submitted, setSubmitted] = useState(false);
  const [hideDaily, setHideDaily] = useState(true);
  const [hideOutflow, setHideOutflow] = useState(true);
  const maxLength = 500;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await DashboardService.getStats();
      const result = res.data;
      if (result && result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("Dashboard stats error:", error);
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Veriler yüklenirken bir sorun oluştu.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    try {
      const res = await FeedBackService.sendFeedback({
        type: category?.value || "suggestion",
        message: feedback
      });
      const result = res.data || res;
      if (result.success === false) {
        toast.current.show({
          severity: "warn",
          summary: "Uyarı",
          detail: "Bildirim gönderilemedi." + (result.message ? " " + result.message : ""),
        }, 3000);
        return;
      } else {
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Bildirim gönderildi. Teşekkürler!",
          life: 3000,
        });
        setSubmitted(true);
        setFeedback("");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const chartData = {
    labels: (stats.salesChartData || []).map(d => d.label),
    datasets: [
      {
        label: 'Satış Hacmi',
        data: (stats.salesChartData || []).map(d => d.value),
        fill: true,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.05)',
        borderWidth: 4,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#10b981',
        pointBorderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: settings.darkMode ? '#1e293b' : '#0f172a',
        padding: 16,
        titleFont: { size: 14, weight: '900' },
        bodyFont: { size: 13, weight: '600' },
        displayColors: false,
        callbacks: {
          label: (context) => `Toplam: ${formatCurrency(context.raw)}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: settings.darkMode ? '#94a3b8' : '#64748b',
          font: { size: 10, weight: '800' }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: settings.darkMode ? '#334155' : '#f1f5f9',
          borderDash: [6, 6]
        },
        ticks: {
          color: settings.darkMode ? '#94a3b8' : '#64748b',
          font: { size: 10, weight: '800' },
          callback: (value) => formatCurrency(value).replace(',00', '').replace('₺', '') + ' ₺'
        }
      }
    }
  };
  const colorStyles = {
    indigo: { bg: "bg-indigo-500/10", text: "text-indigo-600 dark:text-indigo-400" },
    emerald: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400" },
    blue: { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400" },
    rose: { bg: "bg-rose-500/10", text: "text-rose-600 dark:text-rose-400" }
  };

  const SaaSMetricCard = ({ title, value, change, changeType, icon, color }) => {
    const isPositive = changeType === "up";
    const styles = colorStyles[color] || colorStyles.indigo;
    return (
      <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col justify-between h-full group hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-4">
          <span className="text-slate-400 dark:text-slate-500 text-[10px] font-black uppercase tracking-wider">
            {title}
          </span>
          <div className={`w-9 h-9 rounded-xl ${styles.bg} ${styles.text} flex items-center justify-center`}>
            <i className={`pi ${icon} text-base`} />
          </div>
        </div>
        <div>
          <h3 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
            {value}
          </h3>
          {change && (
            <span className="flex items-center gap-1.5 text-xs font-bold">
              <span className={isPositive ? "text-emerald-500" : "text-rose-500"}>
                <i className={`pi pi-arrow-${isPositive ? "up" : "down"}-right text-[10px]`} /> {change}
              </span>
              <span className="text-slate-400 dark:text-slate-500 font-medium">bu ay</span>
            </span>
          )}
        </div>
      </div>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
  };

  const doughnutData = {
    labels: (stats.topSellingProducts || []).slice(0, 4).map(p => p.productName),
    datasets: [
      {
        data: (stats.topSellingProducts || []).slice(0, 4).map(p => p.quantity),
        backgroundColor: [
          '#6366f1', // Indigo
          '#10b981', // Emerald
          '#3b82f6', // Blue
          '#f59e0b', // Amber
        ],
        borderWidth: 0
      }
    ]
  };

  const doughnutOptions = {
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: settings.darkMode ? '#94a3b8' : '#475569',
          font: { size: 10, weight: '700' },
          boxWidth: 8,
          padding: 10
        }
      },
      tooltip: {
        backgroundColor: settings.darkMode ? '#1e293b' : '#0f172a',
        padding: 10,
        titleFont: { size: 11, weight: '800' },
        bodyFont: { size: 11, weight: '600' },
        callbacks: {
          label: (context) => ` Satış: ${context.raw} Adet`
        }
      }
    }
  };

  const dailyFinanceDoughnutData = {
    labels: ['Günlük Gelir', 'Günlük Gider', 'Vadeli Alacak (Bugün)'],
    datasets: [
      {
        data: [stats.todayCollections, stats.todayExpenses, stats.todayDueReceivables],
        backgroundColor: [
          '#10b981', // Emerald
          '#ef4444', // Rose
          '#6366f1'  // Indigo
        ],
        hoverBackgroundColor: [
          '#059669',
          '#dc2626',
          '#4f46e5'
        ],
        borderWidth: 0
      }
    ]
  };

  const dailyFinanceDoughnutOptions = {
    cutout: '75%',
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: settings.darkMode ? '#1e293b' : '#0f172a',
        padding: 10,
        callbacks: {
          label: (context) => ` Tutar: ${formatCurrency(context.raw)}`
        }
      }
    }
  };

  const outflowDoughnutData = {
    labels: ['Aylık Gider', 'Tedarikçi Borçları', 'Bekleyen Alacaklar'],
    datasets: [
      {
        data: [stats.monthExpenses, stats.monthPayables, stats.monthDueReceivables],
        backgroundColor: [
          '#ef4444', // Rose
          '#f59e0b', // Amber
          '#6366f1'  // Indigo
        ],
        hoverBackgroundColor: [
          '#dc2626',
          '#d97706',
          '#4f46e5'
        ],
        borderWidth: 0
      }
    ]
  };

  const outflowDoughnutOptions = {
    cutout: '75%',
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: settings.darkMode ? '#1e293b' : '#0f172a',
        padding: 10,
        callbacks: {
          label: (context) => ` Tutar: ${formatCurrency(context.raw)}`
        }
      }
    }
  };

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen pb-24 font-inter">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      <div className="max-w-[1600px] mx-auto px-4 md:px-6 space-y-6">

        {/* HEADER */}
        <div className="bg-slate-300 dark:bg-slate-800/80 dark:backdrop-blur-md rounded-[2.5rem] p-5 md:p-5 text-white relative overflow-hidden shadow-2xl border border-white/5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl -mr-40 -mt-40" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/50 border border-white/10 text-[12px] font-black uppercase tracking-widest text-emerald-800">
                <i className="pi pi-sparkles" />
                KasamBenim Panel
              </span>

              <h1 className="text-xl md:text-5xl font-black text-slate-800 dark:text-white tracking-tight mt-3 mb-3">
                İşletme Özeti
              </h1>

              <p className="text-slate-700 dark:text-slate-300 text-sm md:text-base font-medium max-w-2xl leading-relaxed">
                Satış, tahsilat, müşteri ve stok durumunuzu tek ekrandan takip edin.
              </p>
            </div>

            <div className="grid grid-cols-2 md:flex gap-2">
              <Button label="Hızlı Satış(F2)" icon="pi pi-shopping-cart" className="!bg-emerald-500 !border-none rounded-2xl font-black" onClick={() => navigate("/sales/productsale")} />
              <Button label="Stok Girişi" icon="pi pi-plus-circle" className="!bg-emerald-600 !border-none rounded-2xl font-black" onClick={() => navigate("/stock/adjustment")} />
              <Button label="Müşteriler(F3)" icon="pi pi-users" className="!bg-indigo-500 !border-none rounded-2xl font-black" onClick={() => navigate("/customers")} />
              <Button label="Ürünler" icon="pi pi-box" className="!bg-amber-500 !border-none rounded-2xl font-black" onClick={() => navigate("/products")} />
              <Button label="Giderler" icon="pi pi-wallet" className="!bg-rose-500 !border-none rounded-2xl font-black" onClick={() => navigate("/expenses")} />
            </div>
          </div>
        </div>

        {/* FOUR MAIN CARDS GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          {/* Card 1: Günlük Finansal Durum */}
          <div
            className="lg:col-span-6 p-5 rounded-2xl shadow-sm border flex flex-col justify-between min-h-[220px]"
            style={{
              backgroundColor: settings.darkMode ? '#1e293b' : '#ffffff',
              borderColor: settings.darkMode ? '#334155' : '#f1f5f9'
            }}
          >
            <div className="flex items-center justify-between mb-2.5">
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">Günlük Durum</h3>
                <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">Bugündeki gelir, gider ve vadeli işlemler</p>
              </div>
              <button
                onClick={() => setHideDaily(!hideDaily)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-all cursor-pointer border-none bg-transparent"
              >
                <i className={`pi ${hideDaily ? 'pi-eye-slash' : 'pi-eye'} text-base`} />
              </button>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 my-auto pt-1">
              <div className="h-[110px] w-[110px] relative flex items-center justify-center">
                <Chart type="doughnut" data={dailyFinanceDoughnutData} options={dailyFinanceDoughnutOptions} className="w-full h-full" />
              </div>
              <div className="flex-1 grid grid-cols-1 gap-2 text-xs w-full">
                <div className="flex justify-between items-center border-b border-slate-50 dark:border-slate-800/40 pb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span className="font-bold text-slate-500 dark:text-slate-400">Günlük Gelir</span>
                  </div>
                  <span className="font-black text-slate-900 dark:text-white">{hideDaily ? '****' : formatCurrency(stats.todayCollections)}</span>
                </div>
                <div className="flex justify-between items-center border-b border-slate-50 dark:border-slate-800/40 pb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                    <span className="font-bold text-slate-500 dark:text-slate-400">Günlük Gider</span>
                  </div>
                  <span className="font-black text-slate-900 dark:text-white">{hideDaily ? '****' : formatCurrency(stats.todayExpenses)}</span>
                </div>
                <div className="flex justify-between items-center pb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
                    <span className="font-bold text-slate-500 dark:text-slate-400">Vadeli Alacak (Bugün)</span>
                  </div>
                  <span className="font-black text-slate-900 dark:text-white">{hideDaily ? '****' : formatCurrency(stats.todayDueReceivables)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Aylık Gider & Ödemeler */}
          <div
            className="lg:col-span-6 p-5 rounded-2xl shadow-sm border flex flex-col justify-between min-h-[220px]"
            style={{
              backgroundColor: settings.darkMode ? '#1e293b' : '#ffffff',
              borderColor: settings.darkMode ? '#334155' : '#f1f5f9'
            }}
          >
            <div className="flex items-center justify-between mb-2.5">
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">Gider & Ödemeler</h3>
                <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">Bu ayın giderleri ve vadeli ödemeleri</p>
              </div>
              <button
                onClick={() => setHideOutflow(!hideOutflow)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-all cursor-pointer border-none bg-transparent"
              >
                <i className={`pi ${hideOutflow ? 'pi-eye-slash' : 'pi-eye'} text-base`} />
              </button>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 my-auto pt-1">
              <div className="h-[110px] w-[110px] relative flex items-center justify-center">
                <Chart type="doughnut" data={outflowDoughnutData} options={outflowDoughnutOptions} className="w-full h-full" />
              </div>
              <div className="flex-1 grid grid-cols-1 gap-2 text-xs w-full">
                <div className="flex justify-between items-center border-b border-slate-50 dark:border-slate-800/40 pb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                    <span className="font-bold text-slate-500 dark:text-slate-400">Aylık Gider</span>
                  </div>
                  <span className="font-black text-slate-900 dark:text-white">{hideOutflow ? '****' : formatCurrency(stats.monthExpenses)}</span>
                </div>
                <div className="flex justify-between items-center border-b border-slate-50 dark:border-slate-800/40 pb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    <span className="font-bold text-slate-500 dark:text-slate-400">Tedarikçi Borçları</span>
                  </div>
                  <span className="font-black text-slate-900 dark:text-white">{hideOutflow ? '****' : formatCurrency(stats.monthPayables)}</span>
                </div>
                <div className="flex justify-between items-center pb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
                    <span className="font-bold text-slate-500 dark:text-slate-400">Bekleyen Alacaklar</span>
                  </div>
                  <span className="font-black text-slate-900 dark:text-white">{hideOutflow ? '****' : formatCurrency(stats.monthDueReceivables)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: En Çok Satan Ürünler */}
          <div
            className="lg:col-span-4 p-5 rounded-2xl shadow-sm border flex flex-col justify-between min-h-[290px]"
            style={{
              backgroundColor: settings.darkMode ? '#1e293b' : '#ffffff',
              borderColor: settings.darkMode ? '#334155' : '#f1f5f9'
            }}
          >
            <div className="mb-2.5">
              <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">En Çok Satanlar</h3>
              <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">Bu ayın en popüler ürünleri</p>
            </div>
            <div className="h-[145px] w-full flex items-center justify-center relative my-auto">
              {(stats.topSellingProducts || []).length > 0 ? (
                <Chart type="doughnut" data={doughnutData} options={doughnutOptions} className="w-full h-full" />
              ) : (
                <span className="text-slate-400 text-xs font-bold italic">Satış bulunmuyor.</span>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs font-bold text-slate-500 border-t border-slate-50 dark:border-slate-700/50 pt-2.5">
              <span>Toplam Satılan Ürün:</span>
              <span className="text-slate-800 dark:text-slate-200 font-black">
                {stats.topSellingProducts.reduce((sum, p) => sum + p.quantity, 0)} Adet
              </span>
            </div>
          </div>

          {/* Card 4: Son Satış İşlemleri */}
          <div
            className="lg:col-span-4 p-5 rounded-2xl shadow-sm border flex flex-col justify-between min-h-[290px]"
            style={{
              backgroundColor: settings.darkMode ? '#1e293b' : '#ffffff',
              borderColor: settings.darkMode ? '#334155' : '#f1f5f9'
            }}
          >
            <div className="mb-2.5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">Son Satışlar</h3>
                <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">Sisteme kaydedilen son işlemler</p>
              </div>
              <Button
                label="Tümü"
                icon="pi pi-arrow-right"
                iconPos="right"
                className="p-button-text p-button-sm font-black p-0 text-indigo-600 dark:text-indigo-400"
                onClick={() => navigate("/sales")}
              />
            </div>
            <div className="overflow-x-auto my-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-700/50 text-slate-400 font-bold">
                    <th className="py-1.5">Tarih</th>
                    <th className="py-1.5">Müşteri</th>
                    <th className="py-1.5 text-right">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.recentSales || []).length > 0 ? (
                    stats.recentSales.slice(0, 4).map((sale, i) => (
                      <tr key={i} className="border-b border-slate-50 dark:border-slate-800/40 last:border-none text-slate-700 dark:text-slate-300 font-semibold">
                        <td className="py-2">{new Date(sale.saleDate).toLocaleDateString("tr-TR")}</td>
                        <td className="py-2 truncate max-w-[90px]">
                          {sale.customerId > 0 ? (
                            <span
                              onClick={() => navigate(`/customers/detail/${sale.customerId}`)}
                              className="font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline cursor-pointer transition-all"
                            >
                              {sale.customerName}
                            </span>
                          ) : (
                            <span className="font-bold text-slate-900 dark:text-white">
                              {sale.customerName}
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${(sale.customerId <= 0 || sale.balanceTotal <= 0)
                              ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400"
                              : sale.paidTotal > 0
                                ? "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"
                                : "bg-rose-50 text-rose-600 dark:bg-rose-950/30 dark:text-rose-400"
                            }`}>
                            {sale.customerId <= 0 ? "TAHSİL EDİLDİ" : sale.balanceTotal <= 0 ? "ÖDENDİ" : sale.paidTotal > 0 ? "KISMİ" : "BEKLİYOR"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="py-6 text-center text-slate-400 italic">Satış bulunmuyor.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Card 5: Stok & Envanter */}
          <div
            className="lg:col-span-4 p-5 rounded-2xl shadow-sm border flex flex-col justify-between min-h-[290px]"
            style={{
              backgroundColor: settings.darkMode ? '#1e293b' : '#ffffff',
              borderColor: settings.darkMode ? '#334155' : '#f1f5f9'
            }}
          >
            <div className="mb-2.5">
              <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">Stok & Envanter</h3>
              <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">Kritik stok seviyesindeki ürünler</p>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-2">
              {(stats.criticalStockProducts || []).length > 0 ? (
                (stats.criticalStockProducts || []).slice(0, 3).map((p, i) => (
                  <div key={i} className="flex justify-between items-center border-b border-slate-50 dark:border-slate-800/40 pb-1.5 last:border-none last:pb-0">
                    <span className="font-bold text-slate-700 dark:text-slate-300 truncate max-w-[180px]">{p.productName}</span>
                    <span className="font-black text-rose-600 dark:text-rose-400">{p.stockQuantity} {p.unit || 'Adet'}</span>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-2 text-center">
                  <i className="pi pi-check-circle text-emerald-500 text-2xl mb-2" />
                  <span className="text-slate-500 dark:text-slate-400 text-xs font-bold">Tüm stoklar normal seviyede!</span>
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs font-bold text-slate-500 border-t border-slate-50 dark:border-slate-700/50 pt-2.5">
              <span>Toplam Ürün Çeşidi:</span>
              <span className="text-slate-800 dark:text-slate-200 font-black">
                {stats.totalProducts} Ürün
              </span>
            </div>
          </div>
        </div>

        {/* FEEDBACK CONTAINER */}
        <div className="bg-slate-900 dark:bg-slate-950/80 rounded-2xl p-6 shadow-sm border border-slate-800">
          <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-black text-white m-0">Geri Bildirim Gönder</h3>
              <p className="text-xs text-slate-400 font-medium mt-1 m-0">Görüş ve önerilerinizi bizimle paylaşın.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 font-bold">Kategori:</span>
              <Dropdown
                value={category}
                options={feedbackTypes}
                optionLabel="label"
                onChange={(e) => setCategory(e.value)}
                className="w-[140px] rounded-xl dark:bg-slate-900 border-none font-bold text-xs"
                itemTemplate={(option) => option && (
                  <div className="flex items-center gap-2 py-0.5">
                    <i className={`${option.icon} ${option.color} text-xs`} />
                    <span className="font-bold text-xs uppercase text-slate-700 dark:text-slate-300">{option.label}</span>
                  </div>
                )}
              />
            </div>
          </div>

          {!submitted ? (
            <div className="flex flex-col gap-3">
              <InputTextarea
                value={feedback}
                onChange={(e) => e.target.value.length <= maxLength && setFeedback(e.target.value)}
                rows={3}
                placeholder="Önerinizi veya tespit ettiğiniz bir hatayı buraya yazabilirsiniz..."
                className="w-full rounded-xl p-3 text-xs bg-slate-950 dark:bg-slate-900 border-none text-white font-medium"
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500 font-bold">{feedback.length}/{maxLength}</span>
                <Button
                  label="Bildirimi İlet"
                  icon="pi pi-send"
                  className="!bg-indigo-600 hover:!bg-indigo-500 !border-none rounded-xl font-black text-xs py-2 px-4 text-white"
                  disabled={!feedback.trim()}
                  onClick={handleSubmitFeedback}
                />
              </div>
            </div>
          ) : (
            <div className="py-6 flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <i className="pi pi-check-circle text-2xl" />
              </div>
              <div className="text-xs font-black text-slate-200 uppercase tracking-wider">Geri bildiriminiz başarıyla iletildi. Teşekkürler!</div>
              <Button label="Yeni Bildirim" className="p-button-text p-button-sm text-slate-300 font-bold" onClick={() => setSubmitted(false)} />
            </div>
          )}
        </div>

      </div>

      <style>{`
        .p-datatable.no-header .p-datatable-thead { display: none !important; }
        .p-datatable.no-header .p-datatable-tbody > tr { border-bottom: 1px solid #f8fafc !important; }
        .p-datatable.no-header .p-datatable-tbody > tr:last-child { border: none !important; }
      `}</style>
    </div>
  );
}
